import { ApiEndpoints } from 'models/apiEndpoints';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { ILESearchQuery } from 'models/report';
import { queryString, queryStringOfSort, queryStringWithSort } from 'helpers/request';
import { IAuditLogSearchFormValues } from 'models/auditLog';
import { ISearchTransactionFormValues } from 'models/form';
import { ISortOptions } from 'models/table';
import { ILicencesSearchForm } from '../models/licences';
import { exportToCSV } from '../utils/csvExport';
import { EArtAncillaryType } from '../constants/ArtAccessoryType';
import { IRegisteredFirearmsSearchQuery } from '../models/registeredFirearm';

export interface ISearchQuery {
  [key: string]: any;
}

const exportLECSV = async (
  LEType: ELegalEntityTypes,
  queryLE: ILESearchQuery,
  sort: ISortOptions | null,
) => {
  const { customFields } = queryLE;

  Object.keys(queryLE).forEach((key: string) => {
    if (!queryLE[key] || key === 'customFields') delete queryLE[key];
  });

  if (LEType) {
    let endpoint = '';
    if (LEType === ELegalEntityTypes.individual) endpoint = ApiEndpoints.EXPORT_ILE_CSV();
    else if (LEType === ELegalEntityTypes.organisation)
      endpoint = ApiEndpoints.EXPORT_OLE_CSV();

    const dataPart = sort !== null ? queryStringWithSort(queryLE, sort) : queryString(queryLE);
    endpoint += `?${dataPart}`;
    const filename = `legal_entity_${LEType}`;
    return exportToCSV(endpoint, filename, customFields);
  }

  return null;
};

const exportFirearmTransactionsCSV = async (
  firearmId: string,
  query: Partial<ISearchTransactionFormValues>,
  sort: ISortOptions | null,
) => {
  const queryPath = sort !== null ? queryStringWithSort(query, sort) : queryString(query);
  const endpoint = `${ApiEndpoints.EXPORT_FIREARM_TRANSACTIONS_CSV(firearmId)}?${queryPath}`;
  const filename = 'firearm_transactions';
  return exportToCSV(endpoint, filename);
};

const exportAuditLogCSV = async (query: Partial<IAuditLogSearchFormValues>) => {
  const endpoint = `${ApiEndpoints.EXPORT_AUDIT_LOG()}?${queryString(query)}`;
  const filename = 'audit_log';
  return exportToCSV(endpoint, filename);
};

const exportLicenceCSV = async (
  query: Partial<ILicencesSearchForm>,
  sort: ISortOptions | null,
) => {
  const sortPart = sort !== null ? `?${queryStringOfSort(sort)}` : '';
  const endpoint = `${ApiEndpoints.EXPORT_LICENCE_CSV()}${sortPart}`;
  const filename = 'licences';
  return exportToCSV(endpoint, filename, query);
};

const exportAncillariesCSV = async (query: ISearchQuery, sort: ISortOptions | null) => {
  const {
    preset,
    catridgeCompositionUniqueId,
    ammunitionTypeUniqueId,
    componentTypeUniqueId,
    componentManufacturerUniqueId,
    capacity,
    ...cleanedQuery
  } = query;

  const sortPart = sort !== null ? `?${queryStringOfSort(sort)}` : '';
  const endpoint = `${ApiEndpoints.EXPORT_ANCILLARY_CSV()}${sortPart}`;
  let filename;

  switch (query.ancillaryDictionaryUniqueId as EArtAncillaryType) {
    case EArtAncillaryType.ACCESSORIES: {
      filename = 'controlled_accessories';
      break;
    }
    case EArtAncillaryType.AMMUNITION: {
      filename = 'ammunition';
      break;
    }
    case EArtAncillaryType.COMPONENTS: {
      filename = 'essential_components';
      break;
    }
    default:
      filename = 'ancillaries';
  }

  return exportToCSV(endpoint, filename, cleanedQuery);
};

const exportRegisteredFirearmsCSV = async (
  queryRegisteredFirearm: IRegisteredFirearmsSearchQuery,
  sort: ISortOptions | null,
) => {
  const { customFields } = queryRegisteredFirearm;
  Object.keys(queryRegisteredFirearm).forEach((key: string) => {
    if (!queryRegisteredFirearm[key] || key === 'customFields') {
      delete queryRegisteredFirearm[key];
    }
  });

  queryRegisteredFirearm.size = 9999;

  const dataPart =
    sort !== null
      ? queryStringWithSort(queryRegisteredFirearm, sort)
      : queryString(queryRegisteredFirearm);

  const endpoint = `${ApiEndpoints.EXPORT_REGISTERED_FIREARMS_CSV()}?${dataPart}`;
  const filename = 'firearms';

  return exportToCSV(endpoint, filename, customFields);
};

export {
  exportLECSV,
  exportAuditLogCSV,
  exportFirearmTransactionsCSV,
  exportLicenceCSV,
  exportAncillariesCSV,
  exportRegisteredFirearmsCSV,
};
